import PowersportsAsset from '../../asset/powersports-asset';
import PowersportsCta from '../../cta/powersports-cta';
import Cta, { CtaStyle } from '../../cta';
import Platform, { PageTypes, PlatformAssetWrapperMap } from '..';
import { createStyleTag, StyleTag } from '../../util/create-style-tag';
// CSS Style files content - Each imported variable is holding the content of the file as a string
import srpStyleContent from './style/srp.css';
import vdpStyleContent from './style/vdp.css';
import AssetWrapper from '../../asset/wrappers';
import { StringValue, WrappedHTMLElement } from '../../util/parse-data';
import { PageNotReady } from '../../errors';
import configuration from '../../configuration';
import { logToPartnerVisit } from '../../util/logging';
import { OlError } from '../../events';

/**
 * Dealerspike V7 Vehicle Detail Page (VDP) Wrapper
 */
export class DealerspikeV7VdpAssetWrapper extends AssetWrapper<PowersportsAsset> {
  getDocumentStyles(): StyleTag[] {
    return [createStyleTag(vdpStyleContent.toString(), 'vdp-style')];
  }

  getRenderLocation(): HTMLElement {
    // We are using a li as element because the CTA list
    // on dealerspike v7 is a list of li element.
    // So to keep the semantically dealers website correct, we gonna use a li
    const ctaListItemElement = this.prependRenderLocationBySelector('ul.ulCallToAction', 'li');
    return ctaListItemElement;
  }

  getVehicleDataFromSpecTable(vehicleDataTypeClass: string) {
    // Try to find any spec table on the page
    const vehicleDataItem = this.container.querySelector<HTMLElement>(`
      div.invUnitInfoTable ul > li.liUnit.${vehicleDataTypeClass} > .unitValue,
      #accordionSpecifications ul > li.liUnit.${vehicleDataTypeClass} > .unitValue
    `);
    if (!vehicleDataItem) return null;
    return new WrappedHTMLElement(vehicleDataItem);
  }

  parseAssetData(): PowersportsAsset {
    const wrappedContainer = new WrappedHTMLElement(this.container);
    return new PowersportsAsset({
      price:
        new WrappedHTMLElement(this.container, '.unitPrice .price.price-price .price-value')
          .getTextContent()
          ?.toFloat() ??
        new WrappedHTMLElement(this.container, '.unitPrice .price.price-pricetext .price-value')
          .getTextContent()
          ?.toFloat(),
      originalPrice: new WrappedHTMLElement(this.container, '.unitPrice .price.price-retail .price-value')
        .getTextContent()
        ?.toFloat(),
      make: wrappedContainer.getAttr('data-unit-make')?.value,
      model: wrappedContainer.getAttr('data-unit-model')?.value,
      year: wrappedContainer.getAttr('data-unit-year')?.value,
      category: (
        wrappedContainer.getAttr('data-unit-category') ||
        this.getVehicleDataFromSpecTable('LiInvcategory')?.getTextContent()
      )?.value,
      subcategory: (
        wrappedContainer.getAttr('data-unit-subcategory') ||
        this.getVehicleDataFromSpecTable('LiInvsubcategory')?.getTextContent()
      )?.value,
      id: wrappedContainer.getAttr('data-unit-id')?.value,
      condition: (
        wrappedContainer.getAttr('data-unit-condition') ||
        this.getVehicleDataFromSpecTable('LiInvcondition')?.getTextContent()
      )?.value,
      color: this.getVehicleDataFromSpecTable('LiInvcolor')?.getTextContent()?.value,
      vin: this.getVehicleDataFromSpecTable('LiInvvin')?.getTextContent()?.value,
      stockNumber: this.getVehicleDataFromSpecTable('LiInvstock-number')?.getTextContent()?.value,
      on_sale: this.container.querySelector('.vehicle-price--savings') != null,
      location: this.getVehicleDataFromSpecTable('LiInvlocation')?.getTextContent()?.value
    });
  }

  createCta(): Cta {
    return new PowersportsCta(this, { style: 'standard' });
  }
}

/**
 * Dealerspike V7 Search Results Page (SRP) Wrapper
 */
export class DealerspikeV7SrpAssetWrapper extends AssetWrapper<PowersportsAsset> {
  private static bannerRendered = false;

  getDocumentStyles(): StyleTag[] {
    return [createStyleTag(srpStyleContent.toString(), 'srp-style')];
  }

  private renderedCtas: Record<string, Cta> = {};

  render(_e?: Event, _forceCtaRender?: boolean): void {
    this.renderAllCtas();
  }

  // List of CTAs to render for this page
  renderAllCtas() {
    (['standard', 'banner'] as CtaStyle[]).forEach((style) => this.renderCta(style));
  }

  renderCta(style: CtaStyle) {
    try {
      if (style === 'banner' && (DealerspikeV7SrpAssetWrapper.bannerRendered || !configuration.enable_srp_cta_widget)) {
        return;
      }
      if (!this.asset || this.asset.filterCTA(configuration.filter_groups)) return;

      // Only create the CTA once for this type
      if (!this.renderedCtas[style]) {
        const cta = new PowersportsCta(this, { style });
        this.renderedCtas[style] = cta;
        cta.render(undefined, true, style);
      } else {
        this.renderedCtas[style].render(undefined, true, style);
      }
      if (style === 'banner') {
        DealerspikeV7SrpAssetWrapper.bannerRendered = true;
      }

      // Report rendering to analytics
      if (this.renderedCtas[style].isRendered()) {
        logToPartnerVisit(this.platform.pageType).catch((error) => console.log(error));
      }
    } catch (error) {
      // If the DOM is not ready yet, retry in the next render cycle
      if (error instanceof PageNotReady) {
        this.platform.onNextCycle(() => this.renderCta(style));
      } else {
        this.platform.eventBus.dispatchEvent(
          new OlError({ detail: { error, msg: `Failed to render DS7 CTA (${style})` } })
        );
      }
    }
  }

  getRenderLocation(style: CtaStyle): HTMLElement {
    return this.getRenderLocationByType(style);
  }

  // Dynamically determine where each CTA should be inserted in the DOM
  getRenderLocationByType(ctaType: CtaStyle): HTMLElement {
    const selectorMap: Record<string, () => HTMLElement> = {
      // Standard CTA placed alongside each asset in the SRP
      standard: () => {
        return this.prependRenderLocationBySelector('.v7list-vehicle__info-column--buttons');
      },

      // Banner CTA placed on top of the SRP
      banner: () => {
        const renderLocation = document.querySelector('.v7list-subheader');
        if (!renderLocation) throw new PageNotReady('Render location not found in the container.');

        const targetLocation = document.createElement('div');
        targetLocation.classList.add('ol-banner-target-element');
        renderLocation.prepend(targetLocation);

        return targetLocation;
      }
    };

    const getTarget = selectorMap[ctaType];
    if (!getTarget) throw new Error(`Unknown CTA type: ${ctaType}`);
    return getTarget();
  }

  getValueFromSpecs(key: string): StringValue | null {
    // Get Spec Element from DOM
    const specElement = this.container.querySelector<HTMLElement>(`.vehicle-specs__item--${key}`);
    // Get Element containing the value we care about, and create a Wrapped HTML Element
    const specValue = new WrappedHTMLElement(specElement, '.vehicle-specs__value');
    // Special case where DSv7 can use '---' as a null value
    if (specValue.getTextContent()?.value === '___') return null;
    // return what we've found
    return specValue.getTextContent();
  }

  parseAssetData(): PowersportsAsset {
    const wrappedContainer = new WrappedHTMLElement(this.container);
    return new PowersportsAsset({
      price:
        new WrappedHTMLElement(
          this.container.querySelector<HTMLElement>('.vehicle-price--current'),
          '.vehicle-price__price'
        )
          .getTextContent()
          ?.toFloat() ??
        new WrappedHTMLElement(
          this.container.querySelector<HTMLElement>('.vehicle-price--current'),
          '.vehicle-price__label'
        )
          .getTextContent()
          ?.toFloat(),
      originalPrice: new WrappedHTMLElement(
        this.container.querySelector<HTMLElement>('.vehicle-price--old'),
        '.vehicle-price__price'
      )
        .getTextContent()
        ?.toFloat(),
      make: wrappedContainer.getAttr('data-unit-make')?.value,
      model: wrappedContainer.getAttr('data-unit-model')?.value,
      category: wrappedContainer.getAttr('data-unit-category')?.value || this.getValueFromSpecs('category')?.value,
      subcategory:
        wrappedContainer.getAttr('data-unit-subcategory')?.value || this.getValueFromSpecs('subcategory')?.value,
      year: wrappedContainer.getAttr('data-unit-year')?.value,
      id: wrappedContainer.getAttr('data-unit-id')?.value,
      color: this.getValueFromSpecs('color')?.value,
      vin: this.getValueFromSpecs('vin')?.value,
      stockNumber: this.getValueFromSpecs('stock-number')?.value,
      condition: this.getValueFromSpecs('condition')?.value,
      on_sale: !!this.container.querySelector('.vehicle-price--savings'),
      location: this.getValueFromSpecs('location')?.value
    });
  }

  static getPageContainer(): HTMLElement {
    const selector = '#v7results';
    const container = document.querySelector<HTMLElement>(selector);
    if (!container) throw new Error(`Page container not found: ${selector}`);
    return container;
  }

  static shouldWatchElements(): boolean {
    return true;
  }
}

/**
 * Dealerspike v7 platform class
 */
export default class DealerspikeV7 extends Platform {
  pageTypeMap: PlatformAssetWrapperMap = {
    [PageTypes.SRP]: DealerspikeV7SrpAssetWrapper,
    [PageTypes.VDP]: DealerspikeV7VdpAssetWrapper
  };

  determinePageType(): PageTypes {
    if (
      // VDPs
      document.querySelector('div#template') != null
    ) {
      return PageTypes.VDP;
    }

    if (document.querySelector('#v7results')) return PageTypes.SRP;
    throw new PageNotReady('Page Type not yet determined.');
  }

  getAssetContainers(): HTMLElement[] {
    let containers;
    if (this.pageType === PageTypes.SRP) {
      containers = Array.from(document.querySelectorAll<HTMLElement>('.v7list-results__item'));
    } else if (this.pageType === PageTypes.VDP) {
      containers = Array.from(document.querySelectorAll<HTMLElement>('#template'));
    } else {
      throw new Error('pageType is not supported by this platform');
    }

    if (!containers?.length) {
      throw new PageNotReady('Containers not yet found');
    }
    return containers;
  }
}
